import { BookOpen, GraduationCap, Home as HomeIcon, LineChart, Settings as SettingsIcon } from "lucide-react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { isAdmin, useMe } from "@/lib/auth";
import { useT, type I18nKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AdminPage } from "@/pages/Admin";
import { ExamLobbyPage } from "@/pages/exam/ExamLobby";
import { ExamResultsPage } from "@/pages/exam/ExamResults";
import { ExamReviewPage } from "@/pages/exam/ExamReview";
import { ExamSessionPage } from "@/pages/exam/ExamSession";
import { GrammarPage } from "@/pages/Grammar";
import { GrammarDetail } from "@/pages/GrammarDetail";
import { PracticeSessionPage } from "@/pages/PracticeSession";
import { HomePage } from "@/pages/Home";
import { LevelFocusPage, LevelHubPage } from "@/pages/Levels";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { LoginPage } from "@/pages/Login";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { SignUpPage } from "@/pages/SignUp";
import { VerifyEmailPage } from "@/pages/VerifyEmail";
import { ProgressPage } from "@/pages/Progress";
import { SettingsPage } from "@/pages/Settings";
import { SkillsCheckpointPage } from "@/pages/SkillsCheckpoint";
import { SkillsPage } from "@/pages/Skills";
import { SkillsDetailPage } from "@/pages/SkillsDetail";
import { SkillsSessionPage } from "@/pages/SkillsSession";
import { SkillsTreePage } from "@/pages/SkillsTree";
import { StudyPage } from "@/pages/Study";
import { WordDetail } from "@/pages/words/WordDetail";
import { WordsList } from "@/pages/words/WordsList";
import { WordsNew } from "@/pages/words/WordsNew";
import { features } from "./lib/features";
import { useIOSMeta } from "./hooks/useIOSMeta";

export function App() {
  useIOSMeta();
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Signup is public — needs to live outside the ProtectedRoute
            so a logged-out visitor can reach it. The page itself
            redirects to "/" if `useMe()` resolves a user, mirroring
            LoginPage's behaviour. */}
        <Route path="/signup" element={<SignUpPage />} />
        {/* Password reset flow. Both pages are pre-auth — the user
            either has no working credential yet (forgot) or is
            following an emailed link (reset). Each guards against
            "user already signed in" itself by redirecting to "/". */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Email verification confirmation page. Public — the user
            might be on a device that isn't signed in (followed link
            from email on a different browser). The page itself
            handles all three states (pending/success/error) and
            decides what action to surface based on signed-in status. */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        />
      </Routes>
      {/* Banner shown when a new SW is installed and ready to take over.
          Kept outside the routes so it appears on every screen, including login. */}
      <PWAUpdatePrompt />
      {/* Companion banner — nudges the user to install as a PWA when
          eligible. Self-suppresses if already installed, on the cooldown,
          or on a non-supporting browser. See `usePWAInstall`. */}
      <PWAInstallPrompt />
    </>
  );
}

function AppShell() {
  const location = useLocation();
  // Hide tab bar on the study flow (full-screen flashcard session),
  // the per-rule practice drill, AND the entire exam flow (lobby +
  // session + results). Full-screen experiences shouldn't compete
  // with bottom nav. Exam routes match `/levels/:level/exam*`.
  const hideTabBar =
    location.pathname.startsWith("/study") ||
    /^\/grammar\/[^/]+\/practice$/.test(location.pathname) ||
    /^\/levels\/[^/]+\/exam/.test(location.pathname) ||
    // Skill detail is a focused promise → start screen with its own
    // sticky CTA at the bottom; the bottom nav competes with it
    // visually and the page already has a back arrow to the journal.
    /^\/skills\/[^/]+$/.test(location.pathname) ||
    // Skill session is a full-screen flow — same rule as Study.
    /^\/skills\/[^/]+\/session$/.test(location.pathname) ||
    // Skill checkpoint is the closing exam — also full-screen.
    /^\/skills\/[^/]+\/checkpoint$/.test(location.pathname);

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-foreground">
      <main className={cn("container flex-1 pt-5", hideTabBar ? "pb-5" : "pb-24")}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/study" element={<StudyPage />} />
          <Route path="/words" element={<WordsList />} />
          <Route path="/words/new" element={<WordsNew />} />
          <Route path="/words/:id" element={<WordDetail />} />
          <Route path="/grammar" element={<GrammarPage />} />
          <Route path="/grammar/:id" element={<GrammarDetail />} />
          <Route path="/grammar/:id/practice" element={<PracticeSessionPage />} />
          {features.skillsEnabled && (
            <>
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/skills/tree" element={<SkillsTreePage />} />
              <Route path="/skills/:slug" element={<SkillsDetailPage />} />
              <Route path="/skills/:slug/session" element={<SkillsSessionPage />} />
              <Route path="/skills/:slug/checkpoint" element={<SkillsCheckpointPage />} />
            </>
          )}
          <Route path="/levels" element={<LevelHubPage />} />
          <Route path="/levels/:level" element={<LevelFocusPage />} />
          <Route path="/levels/:level/exam" element={<ExamLobbyPage />} />
          <Route path="/levels/:level/exam/run" element={<ExamSessionPage />} />
          <Route path="/levels/:level/exam/result" element={<ExamResultsPage />} />
          <Route path="/levels/:level/exam/result/:attemptId/review" element={<ExamReviewPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/admin"
            element={
              <AdminOnly>
                <AdminPage />
              </AdminOnly>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideTabBar && <BottomNav />}
    </div>
  );
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading } = useMe();
  if (isLoading) return null;
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function BottomNav() {
  const t = useT();
  // Store i18n keys (not pre-resolved strings) so the nav re-renders correctly
  // when the language flips at runtime — translating at render time is
  // reactive through the i18n context.
  //
  // Six fixed slots, mode-independent. The previous Skills↔Levels rotate
  // was removed because Skills mode now has its own first-class entry
  // path through the Home body (capability hero + journal link); having
  // a permanent "Levels" slot keeps the hierarchy of routes stable
  // across mode flips. Study lost its slot too — daily sessions are
  // launched from the Home "due today" CTA, so the nav slot is now
  // Vocab (the static list at /words). Settings moved off Home into
  // the rightmost slot.
  type NavItem = {
    to: string;
    labelKey: I18nKey;
    icon: React.ElementType;
    exact: boolean;
  };
  const items: Array<NavItem | undefined> = [
    { to: "/", labelKey: "nav.home", icon: HomeIcon, exact: true },
    // Vocab list — was "Study" (the SRS session). The session is still
    // reachable via Home's "Начать занятие" / "Продолжить" CTAs.
    { to: "/words", labelKey: "nav.vocab", icon: BookOpen, exact: false },
    features.grammarSection
      ? {
          to: "/grammar",
          labelKey: "nav.grammar",
          icon: GraduationCap,
          exact: false,
        }
      : undefined,
    // Levels — always shown, regardless of studyMode. SCENARIOS users
    // still see the Levels page (mode is a queue scope, not a route
    // gate); the Skills journal is reachable from Home.
    // { to: "/levels", labelKey: "nav.levels", icon: Milestone, exact: false },
    { to: "/progress", labelKey: "nav.progress", icon: LineChart, exact: true },
    {
      to: "/settings",
      labelKey: "nav.settings",
      icon: SettingsIcon,
      exact: true,
    },
  ];
  const visible = items.filter((x): x is NavItem => x !== undefined);
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-surface"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="container flex h-[72px] items-center justify-around px-3 pb-4 pt-2">
        {visible.map(({ to, labelKey, icon: Icon, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center justify-center gap-[3px] text-[10px] font-medium",
                isActive ? "text-[color:var(--accent)]" : "text-text-3",
              )
            }
          >
            <Icon className="size-[22px]" strokeWidth={1.8} />
            <span>{t(labelKey)}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
